import { EventLabelOption, EventPanelView, ReportingEventNumberDto } from '@mark43/cad-api';
import { Caution, Divider, HStack, VStack, Box, Text } from '@cad-arc';
import { formatHoursMinutesSeconds } from '../../common/helpers/utils';
import { PersonsOrVehiclesSection } from '../../fr-entity-panels/components/persons-or-vehicles-section';
import { EntityTypes, MergedEntityTypes } from '../../fr-entity-panels/types';
import { SectionTitle } from '../../fr-event-common-components/section-title';
import { ReportingEventNumbersList } from '../../event-panel/tabs/event-details/components/helpers/reporting-event-numbers-list';
import { useGetAgencyType } from '../../common/hooks/use-get-agency-type';
import { LocationSelectValueDisplay } from '../../location-select/components/location-select-value-display';
import { ASSIGNMENT_MODAL_COMMON_SIZE, ASSIGNMENT_MODAL_PADDING_TOP } from '../constants';
import { useCallback } from 'react';
import { locationCautionPriorityToLevel } from '../../common/helpers/styles';
import { CautionLevelAttrId } from '../../socket/stream-types';
import { AssignmentModalDetailsCfs } from './assignment-modal-details-cfs';
import { AssignmentModalDetailsRadioChannel } from './assignment-modal-details-radio-channel';
import { FlagRenderer } from '../../common/components/flag-renderer/flag-renderer';
import classNames from 'classnames';
import styles from './styles.module.css';

export function AssignmentModalDetails({
    eventData,
    unmaskNonPiiFields = false,
}: {
    eventData: EventPanelView;
    // Unmask only the non-PII parts of the held prompt for Datadog Session Replay (RND-53990):
    // the "Location" label (not the address), and the CFS + Radio Channel sections in full.
    // Location value, narrative, persons, vehicles, and plates stay masked.
    unmaskNonPiiFields?: boolean;
}) {
    const primaryAgencyId = eventData.metadata.agencyId;
    const getAgencyType = useGetAgencyType(eventData.metadata.agencyTypeGlobalAttrId);

    const firstRenForPrimaryAgency =
        eventData?.metadata?.agencyIdToReportingEventNumbers[primaryAgencyId]?.[0];

    const reportingEventNumberDto = {
        id: eventData.form.eventId,
        agencyId: primaryAgencyId,
        reportingEventNumber: firstRenForPrimaryAgency,
        agencyEventId: eventData.form.eventId,
        agencyEventNumber: eventData.metadata.agencyEventNumber,
    } as ReportingEventNumberDto;

    const location = eventData.form.location;
    const radioChannel = eventData.form[`${getAgencyType()}PrimaryRadioChannel`];
    const additionalRadioChannels = eventData.form.additionalRadioChannels;
    const timeTransmitted = formatHoursMinutesSeconds(eventData.metadata.timeTransmitted ?? '');

    const getLabelInDisplayedFormat = useCallback((label: EventLabelOption) => {
        const priority = locationCautionPriorityToLevel(
            label.locationCautionPriorityGlobalAttrId as CautionLevelAttrId
        );

        return (
            <Caution
                key={label.value}
                className={classNames(styles['span-lg-text'])}
                data-priority={priority}
                priority={priority}
            >
                {label.label}
            </Caution>
        );
    }, []);

    return (
        <VStack alignItems="left">
            {location && (
                <Box fontSize={ASSIGNMENT_MODAL_COMMON_SIZE}>
                    {/* Location: unmask only the "Location" label, NOT the address value. */}
                    <SectionTitle title="Location" unmask={unmaskNonPiiFields} />
                    <LocationSelectValueDisplay
                        value={location}
                        size={ASSIGNMENT_MODAL_COMMON_SIZE}
                    />
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </Box>
            )}
            <AssignmentModalDetailsCfs
                callForService={eventData?.form.callForService}
                additionalCallForServices={eventData?.form.additionalCallForServices}
                riskLevel={eventData.form.riskLevel}
                eventPriority={eventData.form.priority}
                unmask={unmaskNonPiiFields}
            />
            <AssignmentModalDetailsRadioChannel
                radioChannel={radioChannel}
                additionalRadioChannels={additionalRadioChannels}
                unmask={unmaskNonPiiFields}
            />
            {!!eventData?.form.eventLabels.length && (
                <Box>
                    <SectionTitle title="Labels" />
                    <HStack paddingTop="5px">
                        {eventData?.form.eventLabels.map((eventLabel) =>
                            getLabelInDisplayedFormat(eventLabel)
                        )}
                    </HStack>
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </Box>
            )}
            {!!timeTransmitted && (
                <Box>
                    {/* Narrative stays fully masked — free text may contain PII/CJI (RND-53990). */}
                    <SectionTitle title={`Narrative ${timeTransmitted}`} />
                    <Text as="div" className={classNames(styles['span-lg-text'])}>
                        <FlagRenderer text={eventData.form.narrative} />
                    </Text>
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </Box>
            )}
            {!!eventData.form.vehicles.length && (
                <>
                    <PersonsOrVehiclesSection
                        title="Vehicles"
                        entityType={EntityTypes.VEHICLES}
                        mergedEntityType={MergedEntityTypes.VEHICLES}
                        entitiesList={eventData.form.vehicles}
                        mergedEntitiesList={eventData.form.mergedVehicles}
                        dexDetails={false}
                        size={ASSIGNMENT_MODAL_COMMON_SIZE}
                    />
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </>
            )}
            {!!eventData.form.towVehicles.length && (
                <>
                    <PersonsOrVehiclesSection
                        title="Tow Vehicles"
                        entityType={EntityTypes.VEHICLES}
                        mergedEntityType={MergedEntityTypes.VEHICLES}
                        entitiesList={eventData.form.towVehicles}
                        mergedEntitiesList={eventData.form.mergedTowVehicles}
                        dexDetails={false}
                        size={ASSIGNMENT_MODAL_COMMON_SIZE}
                    />
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </>
            )}
            {(eventData.form.involvedPersons.length ||
                eventData.form.mergedInvolvedPersons.length) && (
                <>
                    <PersonsOrVehiclesSection
                        title="Involved Persons"
                        entityType={EntityTypes.INVOLVED_PERSONS}
                        mergedEntityType={MergedEntityTypes.INVOLVED_PERSONS}
                        entitiesList={eventData.form.involvedPersons}
                        mergedEntitiesList={eventData.form.mergedInvolvedPersons}
                        dexDetails={false}
                        size={ASSIGNMENT_MODAL_COMMON_SIZE}
                        showViewOnMap={false}
                    />
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </>
            )}
            {(eventData.form.reportingParties.length ||
                eventData.form.mergedReportingParties.length) && (
                <>
                    <PersonsOrVehiclesSection
                        title="Reporting Parties"
                        entityType={EntityTypes.REPORTING_PARTIES}
                        mergedEntityType={MergedEntityTypes.REPORTING_PARTIES}
                        entitiesList={eventData.form.reportingParties}
                        mergedEntitiesList={eventData.form.mergedReportingParties}
                        dexDetails={false}
                        size={ASSIGNMENT_MODAL_COMMON_SIZE}
                        showViewOnMap={false}
                    />
                    <Divider style={ASSIGNMENT_MODAL_PADDING_TOP} />
                </>
            )}
            {firstRenForPrimaryAgency && (
                <Box>
                    <SectionTitle title="REN" />
                    <ReportingEventNumbersList
                        reportingEventNumbers={[reportingEventNumberDto]}
                        size={ASSIGNMENT_MODAL_COMMON_SIZE}
                    />
                </Box>
            )}
        </VStack>
    );
}
